import DomainDiagnostics from './DomainDiagnostics';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Input,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ArrowsClockwise as RefreshIcon,
  Copy as CopyIcon,
} from '@phosphor-icons/react';
import api from '../../../lib/api';
import { ResponsiveTable, type ResponsiveColumn } from '../../../components/common/ResponsiveTable';
import { type DnsRecord, type WebsiteProjectDetail } from '../../../types/websiteBuilder';

// ------- Custom Domain panel -------

const domainStatusMeta: Record<string, { label: string; color: 'warning' | 'primary' | 'success' | 'danger' }> = {
  pending_dns: { label: 'Waiting for DNS', color: 'warning' },
  attaching: { label: 'Attaching to CDN', color: 'primary' },
  live: { label: 'Live', color: 'success' },
  failed: { label: 'Failed', color: 'danger' },
};

export const CustomDomainPanel: React.FC<{
  project: WebsiteProjectDetail;
  projectId: string;
}> = ({ project, projectId }) => {
  const queryClient = useQueryClient();
  const [domainInput, setDomainInput] = useState('');

  const invalidateProject = () =>
    queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
  const errMsg = (error: unknown, fallback: string): string => {
    const detail = (error as { responseData?: { error?: string } })?.responseData?.error;
    return typeof detail === 'string' && detail ? detail : fallback;
  };

  const connectDomain = useMutation({
    mutationFn: async () => (await api.websiteBuilder.setCustomDomain(projectId, domainInput)).data,
    onSuccess: () => {
      toast.success('Domain connected — now add the DNS records below');
      invalidateProject();
    },
    onError: (error) => toast.error(errMsg(error, 'Could not connect the domain')),
  });

  const checkDomain = useMutation({
    mutationFn: async () => (await api.websiteBuilder.checkCustomDomain(projectId)).data,
    onSuccess: (data) => {
      if (data.status === 'live') toast.success('Your domain is live!');
      else if (data.status === 'pending_dns') toast('Still waiting on DNS — records can take a while to propagate');
      invalidateProject();
    },
    onError: (error) => toast.error(errMsg(error, 'Domain check failed')),
  });

  const removeDomain = useMutation({
    mutationFn: async () => (await api.websiteBuilder.removeCustomDomain(projectId)).data,
    onSuccess: () => {
      toast.success('Custom domain removed');
      invalidateProject();
    },
    onError: (error) => toast.error(errMsg(error, 'Could not remove the domain')),
  });

  const copyValue = (value: string) => {
    navigator.clipboard?.writeText(value);
    toast.success('Copied');
  };

  const records = project.domain_dns_records || [];
  const meta = domainStatusMeta[project.domain_status] || null;
  const defaultHost = (project.production_url || '').replace('https://', '') || 'the default address';

  const recordColumns: ResponsiveColumn<DnsRecord>[] = [
    {
      key: 'type', header: 'Type', width: '10%',
      sortValue: (row) => row.type,
      render: (r) => <Chip size="sm" variant="soft">{r.type}</Chip>,
    },
    {
      key: 'host', header: 'Host / Name', mobileVariant: 'header',
      sortValue: (row) => row.host,
      render: (r) => (
        <Typography level="body-xs" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.host}</Typography>
      ),
    },
    {
      key: 'value', header: 'Value / Points to', mobileVariant: 'body',
      sortValue: (row) => row.value,
      render: (r) => (
        <Typography level="body-xs" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.value}</Typography>
      ),
    },
    {
      key: 'purpose', header: 'Purpose',
      sortValue: (row) => row.purpose,
      render: (r) => <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{r.purpose}</Typography>,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <Typography level="h3">Custom Domain</Typography>
        {meta && <Chip size="sm" variant="soft" color={meta.color}>{meta.label}</Chip>}
      </Box>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3, maxWidth: 640 }}>
        Serve your website from your own domain instead of{' '}
        <Typography component="span" level="body-sm" sx={{ fontFamily: 'monospace' }}>{defaultHost}</Typography>.
      </Typography>

      {!project.custom_domain ? (
        <Stack spacing={2} sx={{ maxWidth: 560 }}>
          <Box>
            <Typography level="title-sm" sx={{ mb: 1 }}>Your domain</Typography>
            <Input
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="yourcompany.com"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && domainInput.trim().length >= 4) connectDomain.mutate();
              }}
            />
          </Box>
          <Box>
            <Button
              onClick={() => connectDomain.mutate()}
              loading={connectDomain.isPending}
              disabled={domainInput.trim().length < 4 || project.status !== 'deployed'}
            >
              Connect Domain
            </Button>
            {project.status !== 'deployed' && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                Deploy your website first — then connect your domain.
              </Typography>
            )}
          </Box>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            You will get the exact DNS records to add at your registrar (GoDaddy,
            Namecheap, Cloudflare, …). Your site stays online the whole time, and
            the switch happens automatically once the records are in place.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
            <Typography level="title-md" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {project.custom_domain}
            </Typography>
            {project.domain_status === 'live' && (
              <Typography level="body-sm" sx={{ mt: 0.5 }}>
                Your website is live at{' '}
                <a href={`https://${project.custom_domain}`} target="_blank" rel="noreferrer">
                  https://{project.custom_domain}
                </a>
                . DNS caches can take up to an hour to update everywhere.
              </Typography>
            )}
            {project.domain_status === 'failed' && (
              <Alert color="danger" variant="soft" sx={{ mt: 1 }}>
                {project.domain_error || 'Something went wrong — remove the domain and try again.'}
              </Alert>
            )}
          </Sheet>

          {(project.domain_status === 'pending_dns' || project.domain_status === 'attaching') && (
            <>
              <Alert color="warning" variant="soft">
                Add these records at your DNS provider (where {project.custom_domain} is registered).
                We check automatically every 10 minutes and your domain goes live on its own once the
                records are in place — DNS changes can take up to an hour to propagate.
              </Alert>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Both the www and non-www versions of your domain are connected — visitors who type
                the other one are redirected to <strong>{project.custom_domain}</strong> automatically.
                Tip: some DNS providers (e.g. SiteGround, GoDaddy) don&apos;t support ALIAS records at
                the root — in that case make <strong>www</strong> your primary domain here and add a
                redirect from the root to www at your host.
              </Typography>
              <ResponsiveTable<DnsRecord>
                rows={records}
                getRowKey={(r) => `${r.type}-${r.host}`}
                columns={recordColumns}
                rowActions={(r) => (
                  <IconButton size="sm" variant="plain" title="Copy value" onClick={() => copyValue(r.value)}>
                    <CopyIcon />
                  </IconButton>
                )}
                emptyState={
                  <Typography level="body-sm">Preparing your DNS records… check again in a minute.</Typography>
                }
              />
            </>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {project.domain_status !== 'live' && (
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<RefreshIcon />}
                onClick={() => checkDomain.mutate()}
                loading={checkDomain.isPending}
              >
                Check now
              </Button>
            )}
            <Button
              variant="outlined"
              color="danger"
              loading={removeDomain.isPending}
              onClick={() => {
                if (window.confirm(
                  `Disconnect ${project.custom_domain}? Your site stays live at its default address.`,
                )) removeDomain.mutate();
              }}
            >
              Remove Domain
            </Button>
          </Box>
        </Stack>
      )}
      <DomainDiagnostics key={project.custom_domain} projectId={projectId} domain={project.custom_domain} status={project.domain_status} />
    </Box>
  );
};
